import { CareCallGuideDoc, GuideField, GuideScope, GuideSection } from 'src/app/models/care-call-guide.model';

/**
 * Per-clinic scoping of a care call guide document (CHF-E3 §3.6).
 * =========================================================================================
 * THE PROBLEM THIS SOLVES
 * `welby_configuration/care_call_guides/guides/chf_ma` is a GLOBAL document — one `chf_ma` for all
 * 40 clinics, carrying no clinic anywhere. BRD-CLIN-2026-006 is EMORY's standard of care. So the
 * moment CHF-E1 / E2 / E4 seeded the §4.1 tier bands, the symptom gates, the trigger-review block
 * and the diuretic-change questions, every MA at every clinic started seeing them on the CHF call —
 * questions their clinic never agreed to ask, framed in tiers their clinic does not run.
 *
 * `isChfCareCallAlertsEnabled` already existed, but it gates the wrong half: whether findings are
 * ROUTED into the NCM queue. It never touched what is RENDERED, so an out-of-scope clinic saw the
 * whole SOC form and simply got no alert from it — the worst of both, because the MA answers
 * protocol questions that then go nowhere.
 *
 * WHAT THIS DOES
 * Each field (or section) declares which side of the gate it belongs to:
 *
 *   scope: 'soc'      part of BRD-CLIN-2026-006 — rendered ONLY for an onboarded clinic
 *   scope: 'legacy'   what that clinic saw BEFORE the SOC landed — rendered ONLY when NOT onboarded
 *   (absent)          neither: platform content, rendered for everyone
 *
 * The document therefore carries BOTH forms, and one call sees exactly one of them. An out-of-scope
 * clinic gets the pre-CHF-E1 guide back, field for field, rather than a mutilated version of the
 * SOC one — which is why the two checkboxes CHF-E2 replaced with the auto_check severity chain are
 * restored into the seed as `scope: 'legacy'` instead of simply being gone.
 *
 * WHY FILTERED ONCE AT LOAD, NOT IN `isFieldVisible`
 * `isFieldVisible` is a TEMPLATE concern. Filtering there would hide the control while leaving it in
 * the FormGroup and in `guide.sections`, so `collectEscalations`, `collectRoutableFindings` and the
 * note generator would all still walk the SOC fields — an out-of-scope clinic would get §4.1
 * citations in the generated note for questions its MA was never shown. Scoping the document once,
 * before `buildForm`, makes every downstream consumer agree by construction: the SOC fields do not
 * exist on this call.
 *
 * That is also the first of the two independent layers. The second is
 * `careCallAlertsEnabledForClinic$()` in the base component, which is re-read at WRITE time before
 * any alert is sent. Neither is derived from the other on purpose: a bug in this filter still can
 * not put an Emory-protocol alert in another clinic's queue.
 *
 * PURE, AND NEVER MUTATES. The input `doc` is the object the live Firestore listener emitted and is
 * shared with the cache layer; a scoped copy is returned instead.
 */

/** Fields with no `scope` are platform content and always survive — this is the common case. */
const isInScope = (scope: GuideScope | undefined, socEnabled: boolean): boolean => {
  if (scope === undefined) {
    return true;
  }
  if (scope === 'soc') {
    return socEnabled;
  }
  if (scope === 'legacy') {
    return !socEnabled;
  }
  // An unrecognised scope means the document was seeded by a NEWER build than this one. Fail
  // CLOSED: hide it. Showing it would be the one failure mode this whole module exists to prevent,
  // and a missing field is visible to whoever seeded it, while a leaked SOC question is not.
  return false;
};

/**
 * The guide as this clinic should see it.
 *
 * @param doc         the document exactly as Firestore emitted it
 * @param socEnabled  `clients/{id}.featureToggles.isChfCareCallAlertsEnabled` — fails closed
 * @returns a new document; the input is never modified. Returns the input unchanged when nothing
 *          in it is scoped, so the COPD / HTN / T2DM / General guides pay nothing for this.
 */
export const scopeGuideToClinic = (doc: CareCallGuideDoc, socEnabled: boolean): CareCallGuideDoc => {
  if (!doc?.sections?.length || !guideDeclaresScope(doc)) {
    return doc;
  }

  const sections = doc.sections
    .filter((section) => isInScope(section.scope, socEnabled))
    .map((section) => ({
      ...section,
      fields: section.fields.filter((field) => isInScope(field.scope, socEnabled)),
    }))
    // A section left with no fields would render as an empty titled card. Today no section is
    // entirely SOC, so this drops nothing — it exists so that adding one later degrades to "the
    // section is absent" rather than "the section is there and blank".
    .filter((section) => section.fields.length > 0);

  return { ...doc, sections };
};

/** Whether anything in the document is scoped at all. */
export const guideDeclaresScope = (doc: CareCallGuideDoc): boolean =>
  (doc?.sections ?? []).some(
    (section) => section.scope !== undefined || section.fields.some((field) => field.scope !== undefined),
  );

/** Cross-section references are written "section_id.field_id"; a bare id means a sibling. */
const referenceKey = (reference: string, sectionId: string): string => {
  const parts = String(reference).split('.');
  return parts.length === 2 ? `${parts[0]}.${parts[1]}` : `${sectionId}.${parts[0]}`;
};

/**
 * `visible_when`'s watched ids — `field` and every `any_of` entry — and ONLY those. The rule is
 * sibling-scoped at runtime — `isFieldVisible` reads
 * `controlValue(section.id, rule.field)`, so the id is looked up inside the field's own section and
 * a dotted value is never split. Resolving it with `referenceKey` would report
 * `visible_when: { field: 'symptom_screen.sob_at_rest' }` as fine because that field exists
 * SOMEWHERE, while at runtime the control lookup is `weight_check.symptom_screen.sob_at_rest`, which
 * resolves to nothing and leaves the question permanently invisible.
 *
 * Concatenating rather than splitting is what makes that case fail here: the dotted target produces
 * a three-segment key that cannot be in `present`, so it is reported — which is the correct verdict,
 * because a dotted `visible_when` is ALWAYS broken, not merely out of scope.
 *
 * The other five kinds legitimately cross sections (`symptom_gate` is documented as doing so), which
 * is why they keep the shared helper. `any_of` is `field`'s plural and shares its resolution.
 */
const siblingKey = (reference: string, sectionId: string): string => `${sectionId}.${reference}`;

/**
 * Every field id a given field points at, with the key that points there (for the message).
 *
 * MIRRORS `referencesOf` in welby-firestore's `SeedChfCareCallGuideEscalation/scopeInvariants.js`,
 * shape for shape, and must keep doing so: that file's (S4) is the seed-time half of this check, and
 * the two drifting is how a reference kind ends up asserted on one side only. Add a reference kind
 * to the guide schema and it goes in BOTH lists — `compute.*` was added to both when this check was
 * widened, because a `computed` delta whose operand is scoped away renders blank AND stops the
 * `auto_check` band beneath it from ever ticking.
 *
 * The mirror is in the SHAPE, not in the resolution: `visible_when` resolves strictly as a sibling
 * on both sides, everything else through the shared helper. See `siblingKey`.
 *
 * `closed_by` is included even though nothing in this component reads it: it names the `bounds_only`
 * ceiling that clears a band past the range's far edge, so a ceiling the scoping removed silently
 * WIDENS the band it was bounding (BRD §4.1's 60-79% adherence row fires at 100%).
 */
const referencesOf = (field: GuideField, sectionId: string): { key: string; target: string }[] => {
  const references: { key: string; target: string }[] = [];
  const push = (key: string, reference: string) => references.push({ key, target: referenceKey(reference, sectionId) });

  if (field.visible_when?.field) {
    // Sibling-scoped — see `siblingKey`. Not `push`.
    references.push({ key: 'visible_when.field', target: siblingKey(field.visible_when.field, sectionId) });
  }
  // `any_of` resolves exactly like `field` — each entry is a sibling id, never dotted — so every
  // entry is reported on its own. Reporting the LIST would hide the one scoped-away id among the
  // survivors, and the failure is per-id: the rule is OR'd, so a removed entry does not hide the
  // question, it narrows the set of findings that reveal it. On §4.1 Moderate that means the
  // measurement-confirmation prompt stops appearing on one of the two weight axes while still
  // appearing on the other — a gap no one reading the form can see.
  (field.visible_when?.any_of ?? []).forEach((reference, index) => {
    references.push({ key: `visible_when.any_of[${index}]`, target: siblingKey(reference, sectionId) });
  });
  (field.auto_check?.rules ?? []).forEach((rule, index) => {
    if (rule.field) {
      push(`auto_check.rules[${index}].field`, rule.field);
    }
    if (rule.minus_field) {
      push(`auto_check.rules[${index}].minus_field`, rule.minus_field);
    }
  });
  if (field.auto_check?.closed_by) {
    push('auto_check.closed_by', field.auto_check.closed_by);
  }
  if (field.compute?.field) {
    push('compute.field', field.compute.field);
  }
  if (field.compute?.minus_field) {
    push('compute.minus_field', field.compute.minus_field);
  }
  if (field.escalation?.value_from) {
    push('escalation.value_from', field.escalation.value_from);
  }
  (field.escalation?.symptom_gate?.fields ?? []).forEach((reference, index) => {
    push(`escalation.symptom_gate.fields[${index}]`, reference);
  });
  return references;
};

/**
 * Fields kept by the scoping that point at a field the scoping REMOVED.
 *
 * EVERY reference kind, not just `visible_when` — which is the benign one. The two that matter are
 * the ones whose runtime failure is under-escalation, and both are silent:
 *
 * - `escalation.symptom_gate.fields[]` — `isSymptomGateOpen` returns `false` for a field it cannot
 *   find, so the finding keeps `owner: escalation.owner`. On the §4.1 Low weight bands that is
 *   `'ma'`, and the finding is HELD and never routed to the NCM no matter what the patient reports.
 *   The note then reads exactly like a normal held Low finding on an asymptomatic patient.
 * - `escalation.value_from` — `measurementValue` returns `null`, so the alert reaches the queue as
 *   the answer text ("Yes") with no `measurement_value1`, and the NCM loses the delta they are
 *   meant to assess.
 *
 * Reported, never repaired: dropping a `visible_when` would show a question its author meant to keep
 * conditional, and re-pointing a gate is not something a render pass gets to decide. Asserted at
 * seed time by welby-firestore's (S4) — logged here because the LIVE document can be written by a
 * migration that never ran those invariants, which is the one path (S4) cannot cover.
 */
export const findOrphanedScopeReferences = (doc: CareCallGuideDoc): string[] => {
  const present = new Set<string>();
  (doc?.sections ?? []).forEach((section: GuideSection) =>
    (section.fields ?? []).forEach((field: GuideField) => present.add(`${section.id}.${field.id}`)),
  );

  const orphans: string[] = [];
  (doc?.sections ?? []).forEach((section: GuideSection) => {
    (section.fields ?? []).forEach((field: GuideField) => {
      referencesOf(field, section.id).forEach(({ key, target }) => {
        if (!present.has(target)) {
          orphans.push(`${section.id}.${field.id}.${key} -> ${target}`);
        }
      });
    });
  });
  return orphans;
};
